/**
 * shared/hooks/useSearchSuggestions.js (v2.71.61)
 * -----------------------------------------------
 * Client-side suggestion synthesizer for HeroSearch.
 *
 * Builds three suggestion types from cached lookup data:
 *   • location  — main_area + sub_area matches
 *   • bhk       — "<N>BHK in <Area>" when query is like "3 dwa" or "3bhk"
 *   • type      — property type matches from /api/filters
 *
 *   const suggestions = useSuggestions(query, { locations, types });
 *   // → [{ id, kind, label, sub, params: { main_area, bhk, ... } }, ...]
 *
 * The component is responsible for rendering. This hook only produces
 * a stable, type-tagged list that the dropdown can categorize.
 *
 * Backend untouched. Lookup data comes from /api/locations + /api/filters
 * (already fetched + cached by HeroSearch on mount).
 */

import { useMemo } from "react";

// Tunables
const MAX_LOCATIONS = 6;
const MAX_BHK       = 3;
const MAX_TYPES     = 3;

/**
 * Parse "<digit> [bhk]?" pattern out of the query.
 * Returns the BHK number (1..5) or null.
 * Examples: "3" → 3, "3bhk" → 3, "2 bhk dwa" → 2, "dwa" → null.
 */
function extractBhk(q) {
  const m = q.match(/(?:^|\s|^)(\d)\s*bhk?/i) || q.match(/^(\d)\s+/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return n >= 1 && n <= 5 ? n : null;
}

/**
 * Strip the BHK token from the query so the residue can be used to
 * match locations. "3bhk dwa" → "dwa"; "2 bhk uttam" → "uttam".
 */
function stripBhk(q) {
  return q.replace(/(?:^|\s)\d\s*bhk?(?=\s|$)/i, "").replace(/^\d\s+/, "").trim();
}

export default function useSearchSuggestions(query, { locations = [], types = [] } = {}) {
  return useMemo(() => {
    const q = (query || "").trim();
    if (!q) return [];

    const lower = q.toLowerCase();
    const bhk = extractBhk(q);
    const residue = bhk ? stripBhk(q) : q;
    const residueLower = residue.toLowerCase();

    const out = [];

    // ─── locations (main + sub) ─────────────────────────────────
    // Build a flat list with priority: exact-start matches first,
    // then substring matches. Sub-areas are formatted "MainArea · SubArea".
    const locMatches = [];
    (locations || []).forEach((m) => {
      const mname = m.name || m.main_area;
      if (!mname) return;
      if (residueLower && mname.toLowerCase().includes(residueLower)) {
        const isStart = mname.toLowerCase().startsWith(residueLower);
        locMatches.push({
          id: `loc-${mname}`,
          kind: "location",
          label: mname,
          sub: "Locality",
          params: { main_area: mname },
          _rank: isStart ? 0 : 1,
        });
      }
      (m.sub_areas || []).forEach((s) => {
        const sname = (typeof s === "string" ? s : (s?.name || s?.sub_area));
        if (!sname || !residueLower) return;
        if (sname.toLowerCase().includes(residueLower) || `${mname} ${sname}`.toLowerCase().includes(residueLower)) {
          const isStart = sname.toLowerCase().startsWith(residueLower);
          locMatches.push({
            id: `loc-${mname}-${sname}`,
            kind: "location",
            label: `${mname} · ${sname}`,
            sub: "Sub-area",
            params: { main_area: mname, sub_area: sname },
            _rank: isStart ? 0 : 2,
          });
        }
      });
    });
    locMatches.sort((a, b) => a._rank - b._rank);
    const locTop = locMatches.slice(0, MAX_LOCATIONS);
    out.push(...locTop);

    // ─── BHK combinations ───────────────────────────────────────
    // If query has a BHK token AND a location residue, surface "NBHK in <Area>".
    // If query has a BHK token alone, surface "NBHK in <each top-3 area>".
    if (bhk) {
      const targets = locTop.filter((x) => x.kind === "location" && x.params.main_area);
      const used = new Set();
      const pickFrom = targets.length > 0 ? targets : (locations || []).slice(0, MAX_BHK).map((m) => ({
        params: { main_area: m.name || m.main_area },
        label: m.name || m.main_area,
      }));
      pickFrom.slice(0, MAX_BHK).forEach((p) => {
        const area = p.params.main_area;
        if (!area || used.has(area)) return;
        used.add(area);
        out.push({
          id: `bhk-${bhk}-${area}`,
          kind: "bhk",
          label: `${bhk} BHK in ${area}`,
          sub: "BHK + location",
          params: { bhk: `${bhk} BHK`, main_area: area },
        });
      });
      // Generic BHK-only fallback if nothing else matched
      if (!locTop.length) {
        out.push({
          id: `bhk-${bhk}`,
          kind: "bhk",
          label: `${bhk} BHK properties`,
          sub: "BHK",
          params: { bhk: `${bhk} BHK` },
        });
      }
    }

    // ─── property types ─────────────────────────────────────────
    const tMatches = (types || [])
      .filter((t) => t && t.toLowerCase().includes(lower))
      .slice(0, MAX_TYPES)
      .map((t) => ({
        id: `type-${t}`,
        kind: "type",
        label: t,
        sub: "Property type",
        params: { property_type: t },
      }));
    out.push(...tMatches);

    return out;
  }, [query, locations, types]);
}
